import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HomeIcon from '@mui/icons-material/Home';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import InsightsIcon from '@mui/icons-material/Insights';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import FlagIcon from '@mui/icons-material/Flag';
import GavelIcon from '@mui/icons-material/Gavel';

const DRAWER_WIDTH = '86vw';

const navItems = [
  { label: 'Home', to: '/', icon: <HomeIcon sx={{ fontSize: 20 }} /> },
  { label: 'Visao Geral', to: '/visao-geral', icon: <InsightsIcon sx={{ fontSize: 20 }} /> },
  { label: 'Noticias', to: '/news', icon: <NewspaperIcon sx={{ fontSize: 20 }} /> },
  { label: 'Proposito', to: '/proposito', icon: <FlagIcon sx={{ fontSize: 20 }} /> },
  { label: 'Governanca', to: '/governanca', icon: <GavelIcon sx={{ fontSize: 20 }} /> },
  { label: 'Fale Conosco', to: '/fale-conosco', icon: <ContactMailIcon sx={{ fontSize: 20 }} /> }
];

const bottomItems = [
  { label: 'Home', to: '/', icon: <HomeIcon /> },
  { label: 'Noticias', to: '/news', icon: <NewspaperIcon /> },
  { label: 'Sobre', to: '/visao-geral', icon: <InsightsIcon /> },
  { label: 'Contato', to: '/fale-conosco', icon: <ContactMailIcon /> }
];

const normalizePath = (pathname) => {
  if (pathname.startsWith('/news/')) return '/news';
  if (pathname.startsWith('/parceiros/')) return '/parceiros';
  if (pathname.startsWith('/artigo/')) return '/artigo';
  if (pathname.startsWith('/governanca/')) return '/governanca';
  return pathname;
};

const AppShellMobile = ({ footer, children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const scrollerRef = useRef(null);
  const headerRef = useRef(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const pathname = normalizePath(location.pathname);

  useEffect(() => {
    const headerHeight = headerRef.current?.getBoundingClientRect()?.height || 56;
    document.documentElement.style.setProperty('--header-h', `${headerHeight}px`);
  }, [location.pathname]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.scrollTo({ top: 0, behavior: 'auto' });
  }, [location.pathname]);

  const bottomValue = useMemo(() => {
    const found = bottomItems.find((item) => item.to === pathname);
    return found ? found.to : null;
  }, [pathname]);

  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: 'background.default', position: 'relative' }}>
      <AppBar
        ref={headerRef}
        position="fixed"
        color="default"
        elevation={1}
        sx={{
          top: 0,
          left: 0,
          right: 0,
          zIndex: 20000,
          pointerEvents: 'auto'
        }}
      >
        <Toolbar sx={{ minHeight: 56, px: 1.5 }}>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="Abrir menu"
            onClick={() => setDrawerOpen(true)}
            sx={{ mr: 1 }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: '0.02em' }}>
            R5 Inteligencia Digital
          </Typography>
        </Toolbar>
      </AppBar>

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: true, sx: { zIndex: 21000 } }}
        PaperProps={{
          sx: {
            width: DRAWER_WIDTH,
            maxWidth: 360,
            bgcolor: '#0a0a0a',
            color: '#f2f2f2',
            backgroundImage: 'linear-gradient(180deg, #101010 0%, #080808 100%)',
            borderRight: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '0 14px 14px 0',
          }
        }}
      >
        <Box sx={{ px: 2, py: 1.4, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography sx={{ fontSize: '0.68rem', letterSpacing: '0.18em', color: 'rgba(255,255,255,0.55)', fontWeight: 700 }}>
                NAVEGACAO
              </Typography>
              <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, letterSpacing: '0.02em', mt: 0.4 }}>
                R5 Inteligencia Digital
              </Typography>
            </Box>
            <IconButton
              onClick={() => setDrawerOpen(false)}
              aria-label="Fechar menu"
              sx={{
                width: 34,
                height: 34,
                borderRadius: 1.5,
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.14)',
                bgcolor: 'rgba(255,255,255,0.04)'
              }}
            >
              <CloseIcon sx={{ fontSize: 19 }} />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ px: 1.4, py: 1.2 }}>
          <List sx={{ mt: 0.4 }}>
            {navItems.map((item) => (
              <ListItem key={item.to} disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={item.to}
                  onClick={() => setDrawerOpen(false)}
                  selected={pathname === item.to}
                  sx={{
                    borderRadius: 1.5,
                    minHeight: 50,
                    px: 1.2,
                    mb: 0.5,
                    color: 'rgba(255,255,255,0.9)',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.07)',
                    },
                    '&.Mui-selected': {
                      bgcolor: 'rgba(204,0,0,0.22)',
                      borderLeft: '2px solid #cc0000',
                      pl: 1,
                      '& .MuiTypography-root': { color: '#fff', fontWeight: 700 },
                      '& .MuiListItemIcon-root': { color: '#cc0000' },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 34, color: 'rgba(255,255,255,0.62)' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontSize: '1.04rem', fontWeight: 500, letterSpacing: '0.01em' }}
                  />
                  <ChevronRightIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.38)' }} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <Divider sx={{ my: 1.2, borderColor: 'rgba(255,255,255,0.09)' }} />
          <Typography sx={{ px: 1.2, fontSize: '0.73rem', color: 'rgba(255,255,255,0.48)', letterSpacing: '0.1em' }}>
            SYSTEM_NAV v1.0
          </Typography>
        </Box>
      </Drawer>

      <Box
        ref={scrollerRef}
        className="scroller"
        sx={{
          overflowY: 'auto',
          overflowX: 'hidden',
          pt: 'var(--header-h)',
          pb: footer ? 9 : 10,
          minHeight: 'calc(100dvh - var(--header-h))',
          scrollSnapType: 'none !important',
          position: 'relative',
          zIndex: 1,
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-y',
          overscrollBehaviorY: 'auto'
        }}
      >
        {children}
        {footer && <Box id="footer">{footer}</Box>}
      </Box>

      <BottomNavigation
        showLabels
        value={bottomValue}
        onChange={(_, value) => navigate(value)}
        sx={{
          position: 'fixed',
          left: 8,
          right: 8,
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)',
          zIndex: 20000,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          borderRadius: '14px',
          overflow: 'hidden',
          boxShadow: '0 10px 28px rgba(0,0,0,0.14)',
          pointerEvents: 'auto'
        }}
      >
        {bottomItems.map((item) => (
          <BottomNavigationAction
            key={item.to}
            value={item.to}
            label={item.label}
            icon={item.icon}
            component={RouterLink}
            to={item.to}
          />
        ))}
      </BottomNavigation>
    </Box>
  );
};

export default AppShellMobile;
